/**
 * Word Count Optimizer Component
 * 
 * Provides word count optimization for proposals with:
 * - Settings panel with optimization level selector
 * - Preservation priority checkboxes
 * - Section selection interface
 * - Before/after comparison view
 * - Accept/reject controls
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Badge, Input, LoadingSpinner, Modal } from '@tracekit/core';
import {
  Scissors,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  ChevronRight,
  ChevronDown,
  RotateCcw,
  Eye,
  EyeOff,
} from 'lucide-react';
import { optimizeWordCount, applyOptimization, rollbackOptimization } from '../services/rfpApi';
import { getReportJobStatus } from '@tracekit/core';
import { Report } from '@tracekit/core';

interface WordCountOptimizerProps {
  report: Report;
  currentWordCount: number;
  targetWordCount?: number;
  onOptimizationComplete?: () => void;
  onClose?: () => void;
}

interface OptimizationChange {
  section_id: string;
  section_name: string;
  original_text: string;
  optimized_text: string;
  word_reduction: number;
  confidence: number;
  explanation: string;
  impact_assessment: {
    citations_preserved: boolean;
    requirements_covered: boolean;
    technical_accuracy: string;
  };
  original_count: number;
  optimized_count: number;
}

interface OptimizationResult {
  status: string;
  original_count?: number;
  original_total?: number;
  optimized_count?: number;
  optimized_total?: number;
  target_count?: number;
  target_word_count?: number;
  total_reduction?: number;
  reduction_percentage?: number;
  sections_optimized?: string[];
  changes?: OptimizationChange[];
  sections?: OptimizationChange[];
  optimization_session_id?: string;
  session_id?: string;
}

export function WordCountOptimizer({
  report,
  currentWordCount,
  targetWordCount: initialTargetWordCount,
  onOptimizationComplete,
  onClose,
}: WordCountOptimizerProps) {
  const queryClient = useQueryClient();
  
  // Get report ID - handle both report.id and report.report_id
  const reportId = report?.id || report?.report_id;
  
  if (!reportId) {
    return (
      <Card>
        <div className="p-4 text-center text-red-600">
          Error: Report ID is missing. Cannot optimize word count.
        </div>
      </Card>
    );
  }
  
  // Settings state
  const [targetWordCount, setTargetWordCount] = useState<number>(
    initialTargetWordCount || Math.floor(currentWordCount * 0.8)
  );
  const [optimizationLevel, setOptimizationLevel] = useState<'aggressive' | 'moderate' | 'conservative'>('moderate');
  const [selectedSectionIds, setSelectedSectionIds] = useState<string[]>([]);
  const [preservationPriorities, setPreservationPriorities] = useState({
    citations: true,
    requirements: true,
    technical_accuracy: true,
    examples: false,
  });
  
  // Optimization state
  const [optimizationResult, setOptimizationResult] = useState<OptimizationResult | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [acceptedSections, setAcceptedSections] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'side-by-side' | 'unified'>('side-by-side');
  const [showRollbackHistory, setShowRollbackHistory] = useState(false);
  
  // Job tracking state
  const [optimizationJobId, setOptimizationJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<any>(null);
  const [showJobModal, setShowJobModal] = useState(false);
  
  // Get sections from report
  const sections = report.sections || [];
  
  // Initialize selected sections to all if none selected
  useEffect(() => {
    if (selectedSectionIds.length === 0 && sections.length > 0) {
      setSelectedSectionIds(sections.map((s: any) => s.id || s.section_id || `section-${sections.indexOf(s)}`));
    }
  }, [sections, selectedSectionIds.length]);
  
  // Poll job status (runs in background)
  useEffect(() => {
    if (!optimizationJobId) return;
    
    const pollInterval = setInterval(async () => {
      getReportJobStatus(optimizationJobId)
        .then((status) => {
          setJobStatus(status);
          
          if (status.status === 'completed' && status.output_data) {
            clearInterval(pollInterval);
            // Extract results from job output
            const result = {
              status: 'success',
              ...status.output_data,
              original_total: status.output_data.original_total,
              optimized_total: status.output_data.optimized_total,
              sections: status.output_data.sections,
              session_id: status.output_data.session_id,
            };
            setOptimizationResult(result);
            const changes = result.changes || result.sections || [];
            setExpandedSections(new Set(changes.map((c: any) => c.section_id)));
            setShowJobModal(false);
            setOptimizationJobId(null);
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            alert(`Optimization failed: ${status.error_message || 'Unknown error'}`);
            setShowJobModal(false);
            setOptimizationJobId(null);
          }
        })
        .catch((error) => {
          console.error('Error polling job status:', error);
        });
    }, 2000); // Poll every 2 seconds
    
    return () => clearInterval(pollInterval);
  }, [optimizationJobId]);
  
  // Optimization mutation - creates job and shows modal
  const optimizeMutation = useMutation({
    mutationFn: async () => {
      const response = await optimizeWordCount(
        reportId,
        targetWordCount,
        optimizationLevel,
        selectedSectionIds.length === sections.length ? undefined : selectedSectionIds,
        preservationPriorities
      );
      
      // If job-based response, show modal and start polling
      if (response.job_id) {
        setOptimizationJobId(response.job_id);
        setShowJobModal(true);
        setJobStatus({ status: 'queued', progress_data: {} });
        return response; // Return immediately, don't wait
      }
      
      // Direct response (backward compatibility)
      return response;
    },
    onSuccess: (result) => {
      // Only handle direct responses here (non-job responses)
      if (!result.job_id) {
        setOptimizationResult(result);
        const changes = result.changes || result.sections || [];
        setExpandedSections(new Set(changes.map((c: any) => c.section_id)));
      }
    },
    onError: (error: any) => {
      console.error('Optimization failed:', error);
      alert(`Optimization failed: ${error.message || 'Unknown error'}`);
      setShowJobModal(false);
      setOptimizationJobId(null);
    },
  });
  
  // Rollback mutation
  const rollbackMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      return await rollbackOptimization(reportId, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      setOptimizationResult(null);
      setAcceptedSections(new Set());
      onOptimizationComplete?.();
      if (onClose) onClose();
    },
    onError: (error: any) => {
      console.error('Rollback failed:', error);
      alert(`Failed to rollback: ${error.message || 'Unknown error'}`);
    },
  });
  
  // Apply optimization mutation
  const applyMutation = useMutation({
    mutationFn: async (acceptedIds: string[]) => {
      if (!optimizationResult) throw new Error('No optimization result');
      const changes = optimizationResult.changes || optimizationResult.sections || [];
      const sessionId = optimizationResult.optimization_session_id || optimizationResult.session_id;
      if (!sessionId) throw new Error('Session ID not found');
      return await applyOptimization(
        reportId,
        sessionId,
        acceptedIds.length === changes.length ? undefined : acceptedIds
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      onOptimizationComplete?.();
      if (onClose) onClose();
    },
    onError: (error: any) => {
      console.error('Apply optimization failed:', error);
      alert(`Failed to apply optimization: ${error.message || 'Unknown error'}`);
    },
  });
  
  const handleOptimize = () => {
    if (targetWordCount >= currentWordCount) {
      alert('Target word count must be less than current word count');
      return;
    }
    optimizeMutation.mutate();
  };
  
  const handleAcceptAll = () => {
    if (!optimizationResult) return;
    const changes = optimizationResult.changes || optimizationResult.sections || [];
    const allIds = changes.map((c: any) => c.section_id);
    applyMutation.mutate(allIds);
  };
  
  const handleRejectAll = () => {
    setOptimizationResult(null);
    setAcceptedSections(new Set());
  };
  
  const handleRollback = () => {
    if (!optimizationResult) return;
    const sessionId = optimizationResult.optimization_session_id || optimizationResult.session_id;
    if (!sessionId) {
      alert('Cannot rollback: session ID not found');
      return;
    }
    if (confirm('Rollback this optimization and restore original content?')) {
      rollbackMutation.mutate(sessionId);
    }
  };
  
  // Get optimization history from report metadata
  const optimizationHistory = (report?.meta as any)?.optimization_history || [];
  
  const handleAcceptSection = (sectionId: string) => {
    setAcceptedSections(prev => new Set([...prev, sectionId]));
  };
  
  const handleRejectSection = (sectionId: string) => {
    console.log('[WordCountOptimizer] Rejecting section:', sectionId);
    setAcceptedSections(prev => {
      const newSet = new Set(prev);
      const wasAccepted = newSet.has(sectionId);
      newSet.delete(sectionId);
      console.log('[WordCountOptimizer] Section rejection - wasAccepted:', wasAccepted, 'newSet:', Array.from(newSet));
      return newSet;
    });
  };
  
  const handleApplySelected = () => {
    if (!optimizationResult) return;
    const acceptedIds = Array.from(acceptedSections);
    if (acceptedIds.length === 0) {
      alert('Please accept at least one section or reject all changes');
      return;
    }
    applyMutation.mutate(acceptedIds);
  };
  
  const toggleSectionExpanded = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };
  
  const toggleSectionSelection = (sectionId: string) => {
    setSelectedSectionIds(prev => {
      if (prev.includes(sectionId)) {
        return prev.filter(id => id !== sectionId);
      } else {
        return [...prev, sectionId];
      }
    });
  };
  
  const reductionNeeded = currentWordCount - targetWordCount;
  const reductionPercentage = currentWordCount > 0 
    ? ((reductionNeeded / currentWordCount) * 100).toFixed(1)
    : '0';
  
  return (
    <div className="space-y-6">
      {/* Settings Panel */}
      {!optimizationResult && (
        <Card title="Word Count Optimization Settings">
          <div className="space-y-6">
            {/* Current Word Count Display */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Current Word Count</p>
                  <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{currentWordCount.toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Target Word Count</p>
                  <Input
                    type="number"
                    value={targetWordCount}
                    onChange={(e) => setTargetWordCount(parseInt(e.target.value) || 0)}
                    className="text-2xl font-bold w-32 text-right"
                    min={1}
                  />
                </div>
              </div>
              {targetWordCount < currentWordCount && (
                <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Need to reduce by <span className="font-semibold">{reductionNeeded.toLocaleString()}</span> words ({reductionPercentage}%)
                  </p>
                </div>
              )}
            </div>
            
            {/* Optimization Level Selector */}
            <div>
              <label className="block text-sm font-medium mb-3">Optimization Level</label>
              <div className="grid grid-cols-3 gap-3">
                {(['aggressive', 'moderate', 'conservative'] as const).map(level => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => setOptimizationLevel(level)}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      optimizationLevel === level
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="font-semibold capitalize mb-1">{level}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {level === 'aggressive' && '30-40% reduction'}
                      {level === 'moderate' && '15-25% reduction'}
                      {level === 'conservative' && '5-15% reduction'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Preservation Priorities */}
            <div>
              <label className="block text-sm font-medium mb-3">Preservation Priorities</label>
              <div className="space-y-2">
                {([
                  { key: 'citations', label: 'Preserve Citations' },
                  { key: 'requirements', label: 'Preserve Requirements' },
                  { key: 'technical_accuracy', label: 'Preserve Technical Accuracy' },
                  { key: 'examples', label: 'Preserve Examples' },
                ] as const).map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preservationPriorities[key]}
                      onChange={(e) => setPreservationPriorities(prev => ({
                        ...prev,
                        [key]: e.target.checked
                      }))}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">{label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Section Selection */}
            <div>
              <label className="block text-sm font-medium mb-3">
                Sections to Optimize ({selectedSectionIds.length} of {sections.length} selected)
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2">
                {sections.map((section: any, idx: number) => {
                const sectionId = section.id || section.section_id || `section-${idx}`;
                return (
                  <label key={sectionId} className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedSectionIds.includes(sectionId)}
                      onChange={() => toggleSectionSelection(sectionId)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm flex-1">{section.section_name || section.name}</span>
                    <Badge variant="outline">
                      {((section.answer || '').split(/\s+/).filter((w: string) => w.length > 0).length).toLocaleString()} words
                    </Badge>
                  </label>
                );
              })}
              </div>
            </div>
            
            {/* Optimize Button */}
            <Button
              onClick={handleOptimize}
              disabled={optimizeMutation.isPending || targetWordCount >= currentWordCount || selectedSectionIds.length === 0}
              className="w-full"
              size="lg"
            >
              {optimizeMutation.isPending ? (
                <>
                  <LoadingSpinner className="mr-2" />
                  Optimizing...
                </>
              ) : (
                <>
                  <Scissors className="h-5 w-5 mr-2" />
                  Optimize Word Count
                </>
              )}
            </Button>
          </div>
        </Card>
      )}
      
      {/* Job Progress Modal */}
      {showJobModal && optimizationJobId && (
        <Modal
          isOpen={showJobModal}
          onClose={() => {
            // Always allow closing - job continues in background
            setShowJobModal(false);
          }}
          title="Word Count Optimization in Progress"
          size="lg"
        >
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-sm text-blue-900 dark:text-blue-200">
              <p className="font-medium">✓ Optimization job queued successfully</p>
              <p className="mt-1 text-xs">The optimization is running in the background. You can close this modal and continue working - the job will continue processing.</p>
            </div>
            
            {jobStatus && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Badge variant={
                    jobStatus.status === 'completed' ? 'success' :
                    jobStatus.status === 'failed' ? 'error' :
                    jobStatus.status === 'running' ? 'info' : 'warning'
                  }>
                    {jobStatus.status?.toUpperCase() || 'QUEUED'}
                  </Badge>
                  {jobStatus.progress_data && (
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {jobStatus.progress_data.sections_complete || 0} / {jobStatus.progress_data.total_sections || 0} sections
                    </span>
                  )}
                </div>
                
                {jobStatus.progress_data && jobStatus.progress_data.total_sections > 0 && (
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ 
                        width: `${Math.min(
                          ((jobStatus.progress_data.sections_complete || 0) / jobStatus.progress_data.total_sections) * 100,
                          100
                        )}%` 
                      }}
                    />
                  </div>
                )}
                
                <div className="text-sm">
                  {jobStatus.progress_data?.current_section && (
                    <p className="text-gray-700 dark:text-gray-300">
                      Current Section: <span className="font-medium">{jobStatus.progress_data.current_section}</span>
                    </p>
                  )}
                  {jobStatus.status === 'queued' && (
                    <p className="text-gray-600 dark:text-gray-400">Waiting for worker to start...</p>
                  )}
                  {jobStatus.status === 'running' && (
                    <p className="text-gray-600 dark:text-gray-400">Optimizing sections with AI...</p>
                  )}
                  {jobStatus.status === 'completed' && (
                    <p className="text-green-600 dark:text-green-400 font-medium">✓ Optimization complete!</p>
                  )}
                  {jobStatus.status === 'failed' && (
                    <p className="text-red-600 dark:text-red-400">✗ {jobStatus.error_message || 'Optimization failed'}</p>
                  )}
                </div>
              </div>
            )}
            
            <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={() => setShowJobModal(false)}
                variant="outline"
                className="w-full"
              >
                {jobStatus?.status === 'completed' || jobStatus?.status === 'failed' 
                  ? 'Close' 
                  : 'Close (Job continues in background)'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Optimization Results */}
      {optimizationResult && !showJobModal && (
        <Card title="Optimization Results">
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Original</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {(optimizationResult.original_total || optimizationResult.original_count || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Optimized</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {(optimizationResult.optimized_total || optimizationResult.optimized_count || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Reduction</p>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {(optimizationResult.reduction_percentage || 0).toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'side-by-side' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('side-by-side')}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Side-by-Side
                </Button>
                <Button
                  variant={viewMode === 'unified' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('unified')}
                >
                  <EyeOff className="h-4 w-4 mr-1" />
                  Unified
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRollbackHistory(!showRollbackHistory)}
                  title="View optimization history"
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  History
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRejectAll}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Reject All
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleAcceptAll}
                  disabled={applyMutation.isPending}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Accept All
                </Button>
              </div>
            </div>
            
            {/* Optimization History */}
            {showRollbackHistory && optimizationHistory.length > 0 && (
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg">
                <h4 className="font-semibold mb-3">Optimization History</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {optimizationHistory
                    .filter((h: any) => !h.rolled_back)
                    .map((hist: any, idx: number) => (
                      <div
                        key={hist.session_id || idx}
                        className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium">
                            {new Date(hist.timestamp).toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {hist.original_count} → {hist.target_count} words ({hist.optimization_level})
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            by {hist.user}
                          </p>
                        </div>
                        {optimizationResult?.optimization_session_id === hist.session_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleRollback}
                            disabled={rollbackMutation.isPending}
                          >
                            {rollbackMutation.isPending ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600 mr-1" />
                                Rolling back...
                              </>
                            ) : (
                              <>
                                <RotateCcw className="h-3 w-3 mr-1" />
                                Rollback
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
            
            {/* Changes List */}
            <div className="space-y-4">
              {(optimizationResult.changes || optimizationResult.sections || []).map((change: any) => {
                const isExpanded = expandedSections.has(change.section_id);
                const isAccepted = acceptedSections.has(change.section_id);
                
                return (
                  <div
                    key={change.section_id}
                    className={`border-2 rounded-lg ${
                      isAccepted
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <button
                              type="button"
                              onClick={() => toggleSectionExpanded(change.section_id)}
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-5 w-5" />
                              ) : (
                                <ChevronRight className="h-5 w-5" />
                              )}
                            </button>
                            <h3 className="font-semibold text-lg">{change.section_name}</h3>
                            <Badge variant={change.confidence >= 0.8 ? 'default' : 'warning'}>
                              {Math.round(change.confidence * 100)}% confidence
                            </Badge>
                            <Badge variant="outline">
                              -{change.word_reduction} words
                            </Badge>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                            <div>
                              <span className="font-medium">Original:</span>{' '}
                              {change.original_count.toLocaleString()} words
                            </div>
                            <div>
                              <span className="font-medium">Optimized:</span>{' '}
                              {change.optimized_count.toLocaleString()} words
                            </div>
                            <div>
                              <span className="font-medium">Reduction:</span>{' '}
                              {change.word_reduction.toLocaleString()} words
                            </div>
                          </div>
                          
                          {/* Impact Assessment */}
                          <div className="flex items-center gap-4 text-xs mb-3">
                            {change.impact_assessment.citations_preserved && (
                              <Badge variant="outline" className="text-green-600 dark:text-green-400">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Citations Preserved
                              </Badge>
                            )}
                            {change.impact_assessment.requirements_covered && (
                              <Badge variant="outline" className="text-green-600 dark:text-green-400">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Requirements Covered
                              </Badge>
                            )}
                            {change.impact_assessment.technical_accuracy === 'maintained' && (
                              <Badge variant="outline" className="text-green-600 dark:text-green-400">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Technical Accuracy Maintained
                              </Badge>
                            )}
                          </div>
                          
                          {/* Explanation */}
                          <p className="text-sm text-gray-600 dark:text-gray-400 italic mb-3">
                            {change.explanation}
                          </p>
                        </div>
                        
                        <div className="flex flex-col gap-2 ml-4">
                          {isAccepted ? (
                            <Badge variant="default" className="bg-green-500">
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Accepted
                            </Badge>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleAcceptSection(change.section_id);
                                }}
                                type="button"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRejectSection(change.section_id)}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                          {viewMode === 'side-by-side' ? (
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <h4 className="font-semibold mb-2 text-red-600 dark:text-red-400">
                                  Original ({change.original_count} words)
                                </h4>
                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 text-sm max-h-64 overflow-y-auto">
                                  <pre className="whitespace-pre-wrap font-sans">{change.original_text}</pre>
                                </div>
                              </div>
                              <div>
                                <h4 className="font-semibold mb-2 text-green-600 dark:text-green-400">
                                  Optimized ({change.optimized_count} words)
                                </h4>
                                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded p-3 text-sm max-h-64 overflow-y-auto">
                                  <pre className="whitespace-pre-wrap font-sans">{change.optimized_text}</pre>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <h4 className="font-semibold mb-2">Optimized Text</h4>
                              <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded p-3 text-sm max-h-64 overflow-y-auto">
                                <pre className="whitespace-pre-wrap font-sans">{change.optimized_text}</pre>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Apply Selected Button */}
            {acceptedSections.size > 0 && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {acceptedSections.size} of {(optimizationResult.changes || optimizationResult.sections || []).length} sections accepted
                </p>
                <Button
                  onClick={handleApplySelected}
                  disabled={applyMutation.isPending || acceptedSections.size === 0}
                  size="lg"
                >
                  {applyMutation.isPending ? (
                    <>
                      <LoadingSpinner className="mr-2" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Apply Selected Changes
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
